import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { type Gesprekspartner, useContactmomentenVan, useContactmomentMutaties } from '../api/contactmomenten.ts';
import type { Contactkanaal, Contactmoment, Contactrichting } from '../api/typen.ts';
import { useVacature } from '../api/vacatures.ts';
import { perDag, tijd } from '../helpers/formatteer.ts';
import { contactkanaalLabels, contactrichtingLabels } from '../helpers/labels.ts';
import { ContactmomentDialoog } from './ContactmomentDialoog.tsx';
import { type Chatpartner, chatparameter, kanalen, useGesprekBewerken } from './gesprek.tsx';
import { Laadstatus } from './Laadstatus.tsx';
import { useMeldingen } from './Meldingen.tsx';
import {
  BedrijfVerwijzing,
  ContactpersoonVerwijzing,
  GebruikerNaam,
  KandidaatVerwijzing,
  VacatureVerwijzing,
} from './Verwijzingen.tsx';

const soortLabels: Record<Gesprekspartner, string> = {
  kandidaat: 'Kandidaat',
  contactpersoon: 'Contactpersoon',
  bedrijf: 'Bedrijf',
  vacature: 'Vacature',
};

const stap = 50;
const maximum = 500;

interface Props {
  partner: Chatpartner | null;
  onSluit: () => void;
}

/**
 * Een paneel rechts met alle gesprekken met een kandidaat of contactpersoon, of rond een bedrijf of vacature, als
 * chat: oudste bovenaan, uitgaand rechts en inkomend links. Onderaan legt de gebruiker meteen een nieuw gesprek vast.
 * De lijst ernaast blijft bruikbaar, zodat je snel naar een ander gesprek klikt.
 * @param props Met wie de chat is, of `null` als hij dicht is, en wat er bij sluiten gebeurt.
 * @returns Het paneel.
 */
export function GesprekChat({ partner, onSluit }: Props) {
  // Bewaar de laatste partner, zodat het paneel tijdens het dichtschuiven zijn inhoud houdt.
  const laatste = useRef(partner);
  if (partner) {
    laatste.current = partner;
  }
  const getoond = partner ?? laatste.current;
  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={partner !== null}
      sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 460 }, boxShadow: 8 } }}
    >
      {getoond && (
        <Chatinhoud key={chatparameter(getoond)} partner={getoond} open={partner !== null} onSluit={onSluit} />
      )}
    </Drawer>
  );
}

function Chatinhoud({ partner, open, onSluit }: { partner: Chatpartner; open: boolean; onSluit: () => void }) {
  const [limiet, setLimiet] = useState(stap);
  const gesprekken = useContactmomentenVan(partner.soort, partner.id, { limiet });
  const bewerken = useGesprekBewerken();
  const scroller = useRef<HTMLDivElement>(null);
  const afstandTotOnder = useRef<number | null>(null);
  const momenten = useMemo(() => gesprekken.data ?? [], [gesprekken.data]);
  const oudsteEerst = useMemo(() => [...momenten].reverse(), [momenten]);
  const nieuwste = momenten[0]?.id;

  // Spring naar het nieuwste gesprek, behalve na het laden van oudere: dan blijft de positie staan.
  useLayoutEffect(() => {
    const element = scroller.current;
    if (!element) {
      return;
    }
    element.scrollTop =
      afstandTotOnder.current === null ? element.scrollHeight : element.scrollHeight - afstandTotOnder.current;
    afstandTotOnder.current = null;
  }, [momenten.length, nieuwste]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const opToets = (gebeurtenis: KeyboardEvent) => {
      const doel = gebeurtenis.target instanceof HTMLElement ? gebeurtenis.target : null;
      if (gebeurtenis.key === 'Escape' && !doel?.closest('input, textarea, [role="dialog"], [role="presentation"]')) {
        onSluit();
      }
    };
    document.addEventListener('keydown', opToets);
    return () => document.removeEventListener('keydown', opToets);
  }, [open, onSluit]);

  const laadOuder = () => {
    const element = scroller.current;
    afstandTotOnder.current = element ? element.scrollHeight - element.scrollTop : null;
    setLimiet((huidig) => Math.min(huidig + stap, maximum));
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" component="h2" noWrap>
            <Partnernaam partner={partner} />
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {soortLabels[partner.soort]} · alle gesprekken
          </Typography>
        </Box>
        <IconButton aria-label="Chat sluiten" onClick={onSluit}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Box
        ref={scroller}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pb: 2, bgcolor: 'background.default' }}
      >
        <Laadstatus query={gesprekken} leeg="Nog geen gesprekken vastgelegd.">
          {() => (
            <>
              {momenten.length >= limiet && limiet < maximum && (
                <Box sx={{ textAlign: 'center', pt: 1.5 }}>
                  <Button size="small" onClick={laadOuder} disabled={gesprekken.isFetching}>
                    Oudere gesprekken laden
                  </Button>
                </Box>
              )}
              {perDag(oudsteEerst, (moment) => moment.tijdstip).map((dag) => (
                <Box key={dag.sleutel} component="section" aria-label={dag.kop}>
                  <Divider sx={{ my: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {dag.kop}
                    </Typography>
                  </Divider>
                  <Stack spacing={1.5}>
                    {dag.items.map((moment) => (
                      <Bericht key={moment.id} moment={moment} partner={partner} bewerken={bewerken} />
                    ))}
                  </Stack>
                </Box>
              ))}
            </>
          )}
        </Laadstatus>
      </Box>
      {partner.soort === 'kandidaat' || partner.soort === 'contactpersoon' ? (
        <Invoer partner={partner} />
      ) : (
        <NieuwGesprek partner={partner} />
      )}
      {bewerken.dialogen}
    </>
  );
}

function Partnernaam({ partner }: { partner: Chatpartner }) {
  switch (partner.soort) {
    case 'kandidaat':
      return <KandidaatVerwijzing id={partner.id} />;
    case 'contactpersoon':
      return <ContactpersoonVerwijzing id={partner.id} />;
    case 'bedrijf':
      return <BedrijfVerwijzing id={partner.id} />;
    case 'vacature':
      return <VacatureVerwijzing id={partner.id} />;
  }
}

function Bericht({
  moment,
  partner,
  bewerken,
}: {
  moment: Contactmoment;
  partner: Chatpartner;
  bewerken: ReturnType<typeof useGesprekBewerken>;
}) {
  const uitgaand = moment.richting === 'uitgaand';
  const { icoon, kleur } = kanalen[moment.kanaal];
  // In een chat rond een bedrijf of vacature verschilt per gesprek met wie het was.
  const persoon =
    partner.soort === 'bedrijf' || partner.soort === 'vacature'
      ? moment.kandidaatId
        ? { soort: 'kandidaat' as const, id: moment.kandidaatId }
        : moment.contactpersoonId
          ? { soort: 'contactpersoon' as const, id: moment.contactpersoonId }
          : null
      : null;
  const vacature = partner.soort !== 'vacature' ? moment.vacatureId : null;

  return (
    <Stack
      sx={{
        alignItems: uitgaand ? 'flex-end' : 'flex-start',
        '& .bericht-acties': { opacity: { md: 0 }, transition: 'opacity 120ms' },
        '&:hover .bericht-acties, &:focus-within .bericht-acties': { opacity: 1 },
      }}
    >
      <Box
        sx={(thema) => ({
          maxWidth: '88%',
          px: 1.5,
          py: 1,
          borderRadius: 3,
          ...(uitgaand ? { borderBottomRightRadius: 4 } : { borderBottomLeftRadius: 4 }),
          bgcolor: uitgaand ? alpha(thema.palette.primary.main, 0.08) : 'background.paper',
          border: 1,
          borderColor: uitgaand ? alpha(thema.palette.primary.main, 0.18) : 'divider',
        })}
      >
        <Stack
          direction="row"
          spacing={0.75}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap', mb: moment.notitie ? 0.5 : 0 }}
        >
          <Box sx={{ display: 'flex', color: `${kleur}.main` }}>{icoon(14)}</Box>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {contactkanaalLabels[moment.kanaal]}, {contactrichtingLabels[moment.richting].toLowerCase()}
          </Typography>
          {persoon && (
            <Typography variant="caption" color="text.secondary">
              met{' '}
              {persoon.soort === 'kandidaat' ? (
                <KandidaatVerwijzing id={persoon.id} />
              ) : (
                <ContactpersoonVerwijzing id={persoon.id} />
              )}
            </Typography>
          )}
          {vacature && (
            <Typography variant="caption" color="text.secondary">
              over <VacatureVerwijzing id={vacature} />
            </Typography>
          )}
        </Stack>
        {moment.notitie ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {moment.notitie}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Geen notitie
          </Typography>
        )}
      </Box>
      <Stack
        direction={uitgaand ? 'row-reverse' : 'row'}
        spacing={0.5}
        sx={{ alignItems: 'center', mt: 0.25, px: 0.5 }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {tijd(moment.tijdstip)}
          {!bewerken.isVanMij(moment) && (
            <>
              {' · vastgelegd door '}
              <GebruikerNaam id={moment.gebruikerId} />
            </>
          )}
        </Typography>
        {bewerken.magBewerken(moment) && (
          <Box className="bericht-acties" sx={{ display: 'flex' }}>
            <Tooltip title="Wijzigen">
              <IconButton size="small" aria-label="Gesprek wijzigen" onClick={() => bewerken.wijzig(moment)}>
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Verwijderen">
              <IconButton size="small" aria-label="Gesprek verwijderen" onClick={() => bewerken.verwijder(moment)}>
                <DeleteOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Stack>
    </Stack>
  );
}

const kanaalvolgorde = Object.keys(kanalen) as Contactkanaal[];

/** Het invoerveld onderaan de chat: kanaal, richting en notitie, vastgelegd met het tijdstip van nu. */
function Invoer({ partner }: { partner: Chatpartner }) {
  const mutaties = useContactmomentMutaties();
  const { meldFout } = useMeldingen();
  const [kanaal, setKanaal] = useState<Contactkanaal>('telefoon');
  const [richting, setRichting] = useState<Contactrichting>('uitgaand');
  const [notitie, setNotitie] = useState('');
  const bezig = mutaties.legVast.isPending;

  const legVast = () => {
    if (bezig) {
      return;
    }
    mutaties.legVast
      .mutateAsync({
        kanaal,
        richting,
        notitie: notitie.trim() || null,
        kandidaatId: partner.soort === 'kandidaat' ? partner.id : null,
        contactpersoonId: partner.soort === 'contactpersoon' ? partner.id : null,
      })
      .then(() => setNotitie(''))
      .catch(meldFout);
  };

  return (
    <Box
      component="form"
      onSubmit={(gebeurtenis) => {
        gebeurtenis.preventDefault();
        legVast();
      }}
      sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
    >
      <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={kanaal}
          onChange={(_gebeurtenis, waarde: Contactkanaal | null) => waarde && setKanaal(waarde)}
          aria-label="Kanaal"
        >
          {kanaalvolgorde.map((soort) => (
            <ToggleButton key={soort} value={soort} aria-label={contactkanaalLabels[soort]} sx={{ px: 1 }}>
              <Tooltip title={contactkanaalLabels[soort]}>
                <Box component="span" sx={{ display: 'flex' }}>
                  {kanalen[soort].icoon(18)}
                </Box>
              </Tooltip>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={richting}
          onChange={(_gebeurtenis, waarde: Contactrichting | null) => waarde && setRichting(waarde)}
          aria-label="Richting"
        >
          <ToggleButton value="uitgaand" sx={{ px: 1.25, textTransform: 'none' }}>
            Uitgaand
          </ToggleButton>
          <ToggleButton value="inkomend" sx={{ px: 1.25, textTransform: 'none' }}>
            Inkomend
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end' }}>
        <TextField
          multiline
          minRows={1}
          maxRows={8}
          fullWidth
          size="small"
          placeholder="Wat is er besproken?"
          value={notitie}
          onChange={(gebeurtenis) => setNotitie(gebeurtenis.target.value)}
          onKeyDown={(gebeurtenis) => {
            if (gebeurtenis.key === 'Enter' && (gebeurtenis.metaKey || gebeurtenis.ctrlKey)) {
              gebeurtenis.preventDefault();
              legVast();
            }
          }}
          slotProps={{ htmlInput: { 'aria-label': 'Notitie bij het nieuwe gesprek' } }}
        />
        <Tooltip title="Vastleggen (⌘/Ctrl + Enter)">
          <span>
            <IconButton type="submit" color="primary" aria-label="Gesprek vastleggen" disabled={bezig}>
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Wordt vastgelegd met het tijdstip van nu. Een ander tijdstip kies je later via Wijzigen.
      </Typography>
    </Box>
  );
}

/** Rond een bedrijf of vacature is niet vast met wie het gesprek is; dan opent de gewone dialoog. */
function NieuwGesprek({ partner }: { partner: Chatpartner }) {
  const [open, setOpen] = useState(false);
  const vacature = useVacature(partner.soort === 'vacature' ? partner.id : undefined);
  return (
    <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Button fullWidth variant="outlined" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
        Gesprek vastleggen
      </Button>
      <ContactmomentDialoog
        open={open}
        onSluit={() => setOpen(false)}
        bedrijfId={partner.soort === 'bedrijf' ? partner.id : undefined}
        vacature={partner.soort === 'vacature' ? vacature.data : undefined}
      />
    </Box>
  );
}
